import {trackEvent} from '@hconnect/common/logging/Analytics'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {Box, Link, Typography} from '@mui/material'

import {QuoteList} from './QuoteList'
import {useQuote} from './useQuote'

/**
 * First step for AU RMC order stepper if customer has more then 1 quote to select from.
 * Allows the user to select a specific quotation from a list.
 * 
 * Design based on Figma specifications:
 * - Quote list showing quote name and contract number
 * - "Need a new quote?" section with "Contact dispatch" link
 */
export const QuoteStep: React.FC = () => {
  const {t} = useTranslation()

  const {availableQuotes, selectedQuote, setQuote} = useQuote()

  // Handle empty quotes state
  if (!availableQuotes || availableQuotes.length === 0) {
    return (
      <Box
        sx={{
          p: 2,
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="error">
          {t('orderIntake.rmc.steps.quote.noQuotesAvailable', 'No quotes available')}
        </Typography>
      </Box>
    )
  }

  const handleQuoteSelect = (quoteNumber: string) => {
    const selectedQuote = availableQuotes.find((quote) => quote.quoteNumber === quoteNumber)

    trackEvent('rmcQuoteSelected', {
      quoteNumber,
      quoteName: selectedQuote?.quoteName || 'Unknown',
      businessLine: 'RMC'
    })

    setQuote(quoteNumber)
  }

  const handleContactDispatch = () => {
    trackEvent('rmcContactDispatchClicked', {
      businessLine: 'RMC',
      source: 'quoteStep'
    })
    // TODO: Implement contact dispatch functionality
    // This could open a modal, navigate to a contact page, or trigger a phone call
  }

  return (
    <Box>
      {/* Quote selection list */}
      <QuoteList
        quotes={availableQuotes}
        selectedQuoteNumber={selectedQuote?.quoteNumber}
        onSelectQuote={handleQuoteSelect}
      />

      {/* Need a new quote section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 0 0',
          gap: '8px'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#29333d'
          }}
        >
          {t('orderIntake.rmc.steps.quote.needNewQuote', 'Need a new quote?')}
        </Typography>

        <Link
          component="button"
          onClick={handleContactDispatch}
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#016ad4',
            textDecoration: 'none',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
          data-test-id="rmc-quote-contact-dispatch"
        >
          {t('orderIntake.rmc.steps.quote.contactDispatch', 'Contact dispatch')}
        </Link>
      </Box>
    </Box>
  )
}
